import { CapabilityState } from '#gp/app-common/services/subscription.models';
import { CANONICAL_CAPABILITY_KEYS } from './capability-catalogue';

/**
 * The single feature-gating check (issue #563).
 *
 * Synchronous by requirement: gates are read inside digest cycles and during
 * template rendering, and an asynchronous check could never be narrowed back
 * once call sites started awaiting it.
 *
 * The server resolves ENTITLEMENT — flags, generic settings and licence tier —
 * and ships the answer two ways, neither of them a new round trip: rendered
 * into the page by `_allAppsCommonHeadSection.cshtml`, and on every
 * `api/accounts/subscriptionstatus` response. The plan-scoped half is composed
 * here from permissions the client already holds when a plan is open.
 */

/**
 * The gateable capabilities, in #510's matrix order — order matters here
 * because the AdminPortal's capability grid renders in this same order.
 *
 * This is a re-export, not a second literal list: `./capability-catalogue`
 * (dependency-free, so AdminPortalWeb can import it too — see that file's
 * header comment) is the single TypeScript definition of what capabilities
 * exist. It is hand-maintained in step with the C# side's own hardcoded copy
 * in CapabilityKeys (CapabilityModel.cs) — nothing spans THAT boundary, so
 * neither side's test can catch the two languages drifting apart on its own.
 * Server-side truth is GamePlan.FeatureFlags/CapabilityCatalogue.cs.
 */
export const ALL_CAPABILITY_KEYS = CANONICAL_CAPABILITY_KEYS;

export type CapabilityKey = typeof ALL_CAPABILITY_KEYS[number];

/**
 * A user's access level ON A SPECIFIC PLAN — the server's
 * ProjectAccessControlLevel, in which the plan's owner is 100.
 *
 * A BOXED ordinal, not a number, for two reasons.
 *
 * First, the levels are ordered labels that happen to be stored as sortable
 * integers. `Owner - Admin` is not 50, it is nonsense; the only meaningful
 * question is whether one level reaches another. Boxing removes the arithmetic
 * from the type rather than relying on nobody attempting it, and forces the
 * one legitimate operation through `planAccessAtLeast`.
 *
 * Second, it keeps this distinct from every other number on the same 0-100
 * scale. That is not hypothetical: the session service's `permissions.level`
 * is `Math.min(licenceLevel, permissionLevel)`, its licence half caps at
 * Edit (30), and it was passed to `gpCanUseOnPlan` in place of this. Sharing
 * needs Admin (50), so the verdict was `insufficientPlanAccess` for every user
 * alive — plan owners included — and the Add-collaborator button stopped
 * rendering. Both values were honestly typed as an access level. They were not
 * the same fact.
 *
 * `kind` is a real runtime property, so the distinction survives into a
 * debugger and can be asserted at an untyped boundary. The counterpart is
 * `EffectiveEditLevel` in app-workspace/services/session-service.ts.
 */
export interface PlanAccessLevel {
    readonly kind: 'planAccessLevel';
    readonly rank: number;

    /**
     * REFUSES coercion to a primitive, and is never called deliberately.
     *
     * TypeScript blocks arithmetic on this type and blocks comparing it to a
     * raw number, but it PERMITS `a >= b` between two values of the same type,
     * and no type declaration can prevent that. Left alone, such a line
     * compiles and both objects become "[object Object]", so `View >= Owner`
     * is true — an access check failing open.
     *
     * Returning the rank here would fix the answer but leave the habit: the
     * operator would appear to work and `planAccessAtLeast` would stop being
     * used. Throwing makes the comparison functions the only path that works,
     * and names them at the point of failure.
     *
     * `toString` stays readable, so logging, `String(x)` and template literals
     * are unaffected — only arithmetic and relational coercion throw.
     */
    valueOf(): never;
    toString(): string;
}

/**
 * Mirrors ProjectAccessControlLevel / PlanSharingAccessLevel. Duplicated here
 * rather than imported so that app-common does not depend on app-workspace;
 * the values are asserted against the real enum by a web-role test.
 */
export const PlanAccessLevels = {
    Owner: planAccessLevel(100),
    Admin: planAccessLevel(50),
    Edit: planAccessLevel(30),
    Timesheets: planAccessLevel(25),
    View: planAccessLevel(15),
    None: planAccessLevel(0),
};

function planAccessLevel(rank: number): PlanAccessLevel {
    return {
        kind: 'planAccessLevel',
        rank: rank,
        valueOf: () => {
            throw new Error(
                'Compare plan access levels with planAccessAtLeast(actual, required), '
                + 'not with an operator — these are ordered labels, not numbers.');
        },
        toString: () => `planAccessLevel(${rank})`,
    };
}

const KNOWN_PLAN_ACCESS_RANKS: number[] = [100, 50, 30, 25, 15, 0];

/**
 * The only way to obtain a `PlanAccessLevel` from outside this module, and the
 * boundary at which an untyped number becomes a typed level.
 *
 * It VALIDATES, so nothing here is asserted on trust. The intended argument is
 * a level the SERVER reported for this user on this plan — a
 * ProjectAccessControlLevel / PlanSharingAccessLevel straight off an
 * access-control response.
 *
 * Note what validation can and cannot do: it rejects a number that is not on
 * the scale at all, but 30 is a legitimate rank, so it cannot tell a plan
 * access level of 30 from some other quantity that happens to equal 30. Only
 * the type distinction does that, which is why `EffectiveEditLevel` is a
 * separate type minted at its own source.
 */
export function asPlanAccessLevel(serverReportedLevel: number): PlanAccessLevel {
    if (KNOWN_PLAN_ACCESS_RANKS.indexOf(serverReportedLevel) === -1) {
        throw new Error(
            `Not a plan access level: ${serverReportedLevel}. Expected one of `
            + `${KNOWN_PLAN_ACCESS_RANKS.join(', ')} — see ProjectAccessControlLevel.`);
    }

    return planAccessLevel(serverReportedLevel);
}

/**
 * Does `actual` reach `required`? The only ordering operation on these levels,
 * and the reason the rank is not exposed for direct comparison.
 */
export function planAccessAtLeast(actual: PlanAccessLevel, required: PlanAccessLevel): boolean {
    return actual.rank >= required.rank;
}

/**
 * Which capabilities carry a plan-access requirement, and what it is.
 *
 * Only sharing does. #510's rule is that the gate applies to INITIATING a
 * share and never to receiving one, and the plan access level is what tells
 * those apart. Every other capability is already covered by the plan's own
 * edit permission, so gating it twice would be two places to get it wrong.
 */
const PLAN_ACCESS_REQUIREMENTS: { [key: string]: PlanAccessLevel | undefined } = {
    shareByEmail: PlanAccessLevels.Admin,
};

/**
 * Why a capability is or is not available.
 *
 * `notEntitled` and `insufficientPlanAccess` are NOT interchangeable, and
 * #510 presents them differently: the first means the licence does not carry
 * the capability and upgrading would fix that, so the control is shown
 * disabled with an upsell; the second means the user's access level on this
 * plan is too low, which upgrading would not fix, so the control is removed.
 *
 * `unknown` means no snapshot has been supplied at all — the page has no
 * answer yet. It cannot occur on a page rendered by the web role or the dev
 * server, both of which seed one.
 *
 * `notEntitled` also covers a real snapshot that simply does not mention the
 * key — nothing stated an answer, so nothing entitles it. This is distinct
 * from `unknown`: #510 uses the split to choose between removing a control
 * (unknown) and showing it disabled with an upsell (notEntitled). Both leave
 * `enabled: false`, so the split changes only the reason, never the gate.
 */
export type CapabilityReason = 'enabled' | 'notEntitled' | 'insufficientPlanAccess' | 'unknown';

export interface CapabilityVerdict {
    key: CapabilityKey;
    enabled: boolean;
    reason: CapabilityReason;
}

/**
 * One module-level snapshot, seeded from the page global at load and replaced
 * wholesale by each subscriptionstatus response.
 *
 * Read once, NOT on every check — deliberately unlike `gpHasFeature`, which
 * reads its global every call. Two carriers write this set, so a per-call
 * global read would let a stale page-render value undo a fresh refill.
 */
let snapshot: { [key: string]: string } | undefined = toSnapshot(
    typeof __gameplan_capabilities !== 'undefined' ? __gameplan_capabilities : undefined,
);

function toSnapshot(states: CapabilityState[] | null | undefined): { [key: string]: string } | undefined {
    if (!states || !Array.isArray(states)) {
        return undefined;
    }

    const next: { [key: string]: string } = {};
    for (const state of states) {
        if (state && typeof state.key === 'string') {
            next[state.key] = state.value;
        }
    }
    return next;
}

/**
 * Exactly the word "true", case-insensitively. Mirrors
 * CapabilityValues.IsTrue on the server — deliberately strict, so "1", "yes"
 * and "on" are NOT true and a typo in the Portal fails closed.
 */
function isTrue(value: string | undefined): boolean {
    return typeof value === 'string' && value.toLowerCase() === 'true';
}

/** Replaces the snapshot. Called for every subscriptionstatus response. */
export function gpSetCapabilities(states: CapabilityState[] | null | undefined): void {
    snapshot = toSnapshot(states);
}

/** Is this capability available to the current user, independent of any plan? */
export function gpCanUse(key: CapabilityKey): CapabilityVerdict {
    if (!snapshot) {
        return { key, enabled: false, reason: 'unknown' };
    }

    if (!(key in snapshot)) {
        return { key, enabled: false, reason: 'notEntitled' };
    }

    return isTrue(snapshot[key])
        ? { key, enabled: true, reason: 'enabled' }
        : { key, enabled: false, reason: 'notEntitled' };
}

/**
 * Is this capability available to the current user on the plan they are
 * working in? `level` is the user's access level on THAT PLAN as the server
 * reported it — see `PlanAccessLevel`, and mint one with `asPlanAccessLevel`.
 *
 * This can only SUBTRACT. #510 is explicit that entitlements are user-scoped
 * and are never inherited from the plan's owner, so an unentitled verdict is
 * returned untouched however privileged the user is on the plan.
 */
export function gpCanUseOnPlan(key: CapabilityKey, level: PlanAccessLevel): CapabilityVerdict {
    const verdict = gpCanUse(key);
    if (!verdict.enabled) {
        return verdict;
    }

    const required = PLAN_ACCESS_REQUIREMENTS[key];
    if (required === undefined || planAccessAtLeast(level, required)) {
        return verdict;
    }

    return { key, enabled: false, reason: 'insufficientPlanAccess' };
}
